import { useState, useRef, useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import { reilmAPI } from '../../../services/apiClient';

// No stage and no strategy key. The requirement is that the stage comes from the schema for every
// write path ("all new properties added shall automatically be considered New Lead"), and a form that
// still sent stage: 'new_lead' would be the one path where that default never fires - leaving the
// only path that matters untested by the thing meant to guarantee it. The backend default is in
// reilm-be/modules/properties/model.js. strategy has no default and is left absent rather than sent
// as '': PropertyDashboard resolves it through resolveSelectOption (features/pipeline/utils.js),
// which reads an absent value the same way stage does -- null, the select's placeholder.
//
// No contact key either, and this one is a data decision, not a form one. The form posted
// contact: [{ name:'', mobile:'', email:'', role:'', other:'' }] on every create, and that
// placeholder is what 34 of the 39 documents mr-07 deletes actually hold -- the 5 carrying a real
// value were all created in May and June. The count is what forces the order: the first scope of
// this collection found 36 and the 24.08 census found 39, the same set on two days, growing with
// every property created and no one typing a contact. So the deletion cannot run while the form
// still posts the key, or it undoes itself from the next create onward. Nothing reads the key back:
// mr-05 took the pipeline column, mr-06 took the Property Dashboard card, and
// modules/properties/validation.js refuses the path on PATCH.
const initialForm = {
  propertyType: '',
  price: '',
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
    coordinates: { lat: null, lon: null },
  },
  details: {
    beds: '',
    baths: '',
    sqft: '',
    lotSize: '',
    yearBuilt: '',
    hoaFee: '',
    sewer: '',
    parking: '',
    amenities: '',
    floodZone: '',
    tax: { amount: '', year: '' },
    schools: {
      elementary: '',
      elementary_state_rate: '',
      middle: '',
      middle_state_rate: '',
      high: '',
      high_state_rate: '',
    },
  },
};

// Keys are the provider's codes, values are the only four strings the property model's enum accepts,
// so this map may never emit anything else -- a value outside the four is rejected on save, not shown
// with a warning. The provider's vocabulary is the wider one and it is open-ended: 22 sampled detail
// responses on 11.08 returned `single_family`, `condos`, `apartment`, `industrial` and `other`. Only
// `condos` was a missed translation -- the map had the singular, so condos imported with the type
// blank while every other field filled. The rest have no counterpart among the four and deliberately
// map to nothing, for the user to pick; an unset type is savable for exactly this reason.
const PROPERTY_TYPE_LABELS = {
  single_family: 'Single Family',
  multi_family: 'Multi Family',
  condo: 'Condo',
  condos: 'Condo',
  townhouse: 'Townhouse',
};

// description.baths is the provider's own rounded integer; description.baths_consolidated carries
// the exact value ("3.5") and disagrees with it on 145 of 533 sampled detail responses (27.2%), 141
// of those rounded up. It is always a string in every sample, but three carry a non-numeric one
// ("2.5+", "1.5+") rather than a plain number -- and details.baths on the property model is
// `{ type: Number }`, so a raw "2.5+" reaching Property.create throws a CastError there, which is a
// 400 that loses the whole import, all sixteen mapped fields, not just this one (same failure shape
// as the propertyType comment above). Use the consolidated value only when it parses to a finite
// number; otherwise fall back to the rounded field exactly as before.
const resolveBaths = (description) => {
  const consolidated = description?.baths_consolidated;
  // Every sampled record carries this as a string; anything else (an array, a boolean, a bare
  // number) must not reach Number() at all -- Number([]) is 0 and Number(true) is 1, both finite,
  // so without this guard they would silently read as a real bath count instead of falling back.
  // Same reasoning as the backend's parseBathsConsolidated (scripts/lib/subject-baths-census.js),
  // which this mirrors.
  if (typeof consolidated !== 'string') return description?.baths ?? '';
  const trimmed = consolidated.trim();
  const parsed = Number(trimmed);
  // Number('') is 0, not NaN, and Number() trims whitespace before coercing -- so a whitespace-only
  // string parses to a finite 0 same as an empty one. Checking the trimmed string for emptiness
  // catches both.
  const usable = trimmed !== '' && Number.isFinite(parsed);
  return usable ? parsed : (description?.baths ?? '');
};

export function mapApiToForm(d, fallbackLabel) {
  const allSchools = d?.schools?.schools ?? [];
  const find = (level) => allSchools.find(s => s.education_levels?.[0] === level);
  const cleanName = (s, suffix) => s?.name?.replace(new RegExp(suffix, 'i'), '').trim() ?? '';
  const transformDetails = (details) => {
    if (!details?.length) return {};
    return details.reduce((acc, entry) => {
      // A real detail response can carry a null entry, or an entry with no category. Every other
      // read in this function is defensive about a missing field; skipping here instead of throwing
      // keeps one bad entry from losing beds/baths/sqft/price/schools -- everything mapApiToForm
      // produces -- since a throw here propagates out of the whole function before any field is
      // assigned.
      const category = entry?.category;
      if (!category) return acc;
      acc[category.toLowerCase().replace(/\s+/g, '_')] = entry.text;
      return acc;
    }, {});
  }

  return {
    address: {
      street: d?.location?.address?.line ?? fallbackLabel,
      city: d?.location?.address?.city ?? '',
      state: d?.location?.address?.state_code ?? '',
      zip: d?.location?.address?.postal_code ?? '',
      coordinates: {
        lat: d?.location?.address?.coordinate?.lat ?? null,
        lon: d?.location?.address?.coordinate?.lon ?? null,
      },
    },
    propertyType: PROPERTY_TYPE_LABELS[d?.description?.type] ?? '',
    price: d?.list_price ?? '',
    history: d?.property_history?.map( h => {
        return { date: h.date, event_name: h.event_name, price: h.price, mls_source: h.source_name }
    }),
    details: {
      beds: d?.description?.beds ?? '',
      baths: resolveBaths(d?.description),
      sqft: d?.description?.sqft ?? '',
      lotSize: d?.description?.lot_sqft
        ? (d.description.lot_sqft / 43560).toFixed(2)
        : '',
      yearBuilt: d?.description?.year_built ?? '',
      parking: d?.description?.garage != null ? 'garage' : '',
      hoaFee: d?.hoa?.fee ?? '',
      floodZone: d?.local?.flood?.fema_zone?.[0] ?? '',
      sewer: '',
      amenities: transformDetails(d?.details),
      tax: {
        amount: d?.tax_history?.[0]?.tax ?? '',
        year: d?.tax_history?.[0]?.year ?? '',
      },
      schools: {
        elementary: cleanName(find('elementary'), 'Elementary School'),
        elementary_state_rate: find('elementary')?.rating ?? '',
        middle: cleanName(find('middle'), 'Middle School'),
        middle_state_rate: find('middle')?.rating ?? '',
        high: cleanName(find('high'), 'High School'),
        high_state_rate: find('high')?.rating ?? '',
      },
    },
  };
}

// react-select styles — brand token aligned
const rsStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    height: '40px',
    borderRadius: '8px',
    borderColor: '#DAE4D5',
    boxShadow: 'none',
    fontFamily: 'Figtree, sans-serif',
    fontSize: '13px',
    '&:hover': { 
      borderColor: state.isDisabled ? '#DAE4D5' : '#4CBC18' 
    },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 12px' }),
  singleValue: (base) => ({ ...base, color: '#17270D', fontFamily: 'Figtree' }),
  placeholder: (base) => ({ ...base, color: '#DAE4D5', fontFamily: 'Figtree', fontSize: '13px', fontWeight: 'bold' }),
  option: (base, state) => ({
    ...base,
    fontFamily: 'Figtree',
    fontSize: '13px',
    paddingLeft: '28px',
    backgroundColor: state.isFocused ? '#EEFBE7' : 'white',
    color: '#17270D',
    '&:active': { backgroundColor: '#EEFBE7' },
  }),
  groupHeading: (base) => ({
    ...base,
    color: '#a7c199',
    fontFamily: 'Figtree',
    fontSize: '12px',
    fontWeight: 600,
    paddingLeft: '12px',
    textTransform: 'none',
  }),
  menu: (base) => ({
    ...base,
    border: '1px solid #7dbd42',
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    zIndex: 999,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
};

// Same bounds as reilm-be's RECOMPUTE_CRITERIA_LIMITS and PropertyDashboard.jsx's own BEDS_FIELD_MIN/
// MAX etc: these feed calcScore/compAdjustments directly whenever arv.criteria's own min is empty, so
// an unchecked typo at creation time reaches the ARV estimate unbounded.
// beds carries integer: true, same as fieldLimits.js's own beds entry -- real provider comp data is
// beds-integer-only, so a fractional value has no comp to match and 400s on the backend's
// assertRecomputeCriteriaBody rule. baths legitimately takes 0.5 steps and stays without the flag.
const DETAIL_FIELD_BOUNDS = {
  beds: { min: 0, max: 15, integer: true },
  baths: { min: 0, max: 15 },
  sqft: { min: 0, max: 10000 },
  yearBuilt: { min: 1800, max: 2099 },
};

// Reject, not clamp -- same rule as PropertyDashboard.jsx's Major Systems years. Blank passes: every
// one of these fields is optional here.
const isDetailFieldValid = (field, value) => {
  if (value === '' || value == null) return true;
  const { min, max, integer } = DETAIL_FIELD_BOUNDS[field];
  const n = Number(value);
  if (!Number.isFinite(n) || n < min || n > max) return false;
  return !integer || Number.isInteger(n);
};

// Color scale matches assets/arv/school-ratings-color.svg
const getScoreBg = (n) => {
  if (!n || n <= 0) return '#C4CCBE';
  if (n <= 4)       return '#4F7A35';
  if (n <= 7)       return '#2F4A1F';
  return '#17270D';
};

function Label({ children, muted }) {
  return (
    <label className={`block text-[13px] mb-[6px] ${muted ? 'text-brand-secondary' : 'text-brand-dark'}`}>
      {children}
    </label>
  );
}

function Field({ label, muted, children }) {
  return (
    <div>
      <Label muted={muted}>{label}</Label>
      {children}
    </div>
  );
}

function SectionHeader({ children }) {
  return <h3 className="text-[15px] text-brand-secondary mb-4 mt-2">{children}</h3>;
}

// "Any metadata values not imported shall display em dash". A placeholder and not a value: these are
// controlled inputs, so a dash written into value would have to be deleted before typing, and an
// untouched form would save the literal string "—" into details. Spec values: 13px regular #8A9A85,
// which is brand.placeholder in tailwind.config.js and is a different token from the #DAE4D5 the
// form's other placeholders used. Address is excluded - it is typed, not imported, and keeps its own
// prompt.
const EM_DASH = '—';
const emDashCls = 'w-full h-10 rounded-md border border-brand-muted bg-white px-3 text-[13px] text-brand-dark placeholder:text-brand-placeholder outline-none focus:border-brand-hover transition-colors hover:border-brand-hover disabled:border-brand-muted';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function AddOpportunity({ onClose, save }) {
  const [form, setForm] = useState(initialForm);
  const [addressValue, setAddressValue] = useState(null);
  const [saveError, setSaveError] = useState(null);
  // Mirrors savingRef for rendering only -- the ref is checked and set synchronously (see below) and
  // is what actually blocks a concurrent call, but a ref write alone triggers no re-render. Composing
  // savingRef.current straight into `disabled` would be inert on the first save specifically: the
  // handler's own setSaveError(null) is a no-op when saveError is already null, so nothing re-renders
  // between the ref being set and the request settling, and the disabled attribute would never reach
  // the DOM until it was too late to matter. This state exists only to make that render happen.
  const [saving, setSaving] = useState(false);
  // A failed address lookup used to render as "No results", identical to a genuine no-match. That
  // is how a broken lookup reached a review round with the wrong cause attached to it: from the
  // screen there was nothing to tell apart an unreachable API, a rejected request and an address
  // that simply does not exist.
  const [searchFailed, setSearchFailed] = useState(false);
  // Same shape as searchFailed, one call later: the detail lookup used to swallow its failure into a
  // fallback that only set the street, leaving city/state/zip/price/beds/baths/sqft blank with nothing
  // on screen to say why. The backend now retries this call once (route.js isDownstreamFlake) for the
  // known intermittent shape, so this only fires once that retry is also exhausted.
  const [detailFailed, setDetailFailed] = useState(false);
  const debounceTimer = useRef(null);
  // A ref, not state: three rapid clicks on Finish produced three POSTs and three properties, and
  // nothing downstream stops it -- no idempotency key on the client, no de-dupe in the shared axios
  // instance, no rate limit on the route, an unconditional Property.create in the service, no unique
  // index on the collection. Two clicks can land in the same tick, before a state update from the
  // first has flushed, so a state-based latch would still let the second call through. This has to be
  // set synchronously before the first await in handleFinish.
  const savingRef = useRef(false);

  const addressFilled = !!form.address.street;

  // Scoped setters for nested sub-objects
  const setTop = (field) => (e) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const setDetails = (field) => (e) =>
    setForm(prev => ({ ...prev, details: { ...prev.details, [field]: e.target.value } }));

  // Same reject-on-blur UX as PropertyDashboard.jsx's Major Systems years: silently clears back to
  // blank rather than showing a new error state this screen has no drawn frame for.
  const clearDetailIfInvalid = (field) => () =>
    setForm(prev => isDetailFieldValid(field, prev.details[field])
      ? prev
      : { ...prev, details: { ...prev.details, [field]: '' } });

  const setTax = (field) => (e) =>
    setForm(prev => ({
      ...prev,
      details: { ...prev.details, tax: { ...prev.details.tax, [field]: e.target.value } },
    }));

  const setSchool = (field) => (e) =>
    setForm(prev => ({
      ...prev,
      details: {
        ...prev.details,
        schools: { ...prev.details.schools, [field]: e.target.value },
      },
    }));

  // The generic setDetails would overwrite the whole details.amenities dict with the typed string,
  // discarding every other category transformDetails put there -- homeowners_association among them,
  // which PropertyDashboard reads back out on the saved property (details.amenities?.homeowners_association).
  // Update just the one key the field edits so a later Finish still carries the rest.
  const setAmenitiesText = (e) => setForm(prev => ({
    ...prev,
    details: {
      ...prev.details,
      amenities: prev.details.amenities && typeof prev.details.amenities === 'object'
        ? { ...prev.details.amenities, amenities_and_community_features: [e.target.value] }
        : e.target.value,
    },
  }));

  // ---------------------------------------------------------------------------
  // Address async select
  // ---------------------------------------------------------------------------
  const loadOptions = useCallback((inputValue, callback) => {
    clearTimeout(debounceTimer.current);
    if (inputValue.length <= 3) { callback([]); return; }
    debounceTimer.current = setTimeout(async () => {
      try {
        const { data } = await reilmAPI.searchAddress(inputValue);
        // RapidAPI's own envelope is { data: {...}, status, message }; our backend re-wraps that
        // as { data: r.data } before it reaches here, so the real payload sits three levels deep.
        const opts = data?.data?.data?.autocomplete
          ?.filter(a => a.area_type === 'address')
          ?.map(a => ({ value: a.id, label: a?.full_address?.[0] })) ?? [];
        setSearchFailed(false);
        callback(opts);
      } catch {
        setSearchFailed(true);
        callback([]);
      }
    }, 800);
  }, []);

  const onAddressChange = async (option) => {
    if (!option) {
      setAddressValue(null);
      setForm(prev => ({ ...prev, address: initialForm.address }));
      setDetailFailed(false);
      return;
    }
    setAddressValue(option);
    try {
      const { data } = await reilmAPI.realtorPropertyDetail(option.value);
      // Same triple-nesting as searchAddress above -- confirmed live 14.08 for both endpoints.
      const mapped = mapApiToForm(data?.data?.data, option.label);
      setForm(prev => ({ ...prev, ...mapped }));
      setDetailFailed(false);
    } catch (err) {
      // Same guard as handleFinish: a 401 here means the interceptor is already redirecting to
      // login, so showing a "details unavailable" message would flash the wrong cause on the way out.
      if (err?.response?.status === 401) return;
      // Fallback: at minimum set street from the selected label. Everything else stays blank, so the
      // message below is what tells the user that's a failed import and not a listing with no data.
      setForm(prev => ({
        ...prev,
        address: { ...prev.address, street: option.label },
      }));
      setDetailFailed(true);
    }
  };

  // ---------------------------------------------------------------------------
  // Collapse toggle and save
  // ---------------------------------------------------------------------------
  const handleCollapseToggle = () => {
    // Second entry point to handleFinish -- without this check a click here during the in-flight
    // window bypasses the ref guard inside handleFinish entirely, since it would be the guard's very
    // first read for this call.
    if (savingRef.current) return;
    if (!addressFilled) { onClose(); return; }
    handleFinish();
  };

  const handleFinish = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    try {
      // A provider value lands in form.details straight from mapApiToForm and can reach here
      // without ever firing a field's own onBlur, so the blur guard above is not enough on its own.
      // null, not '' -- the same value PropertyDashboard.jsx's own guard sends, and the shape
      // assertValidMajorSystemYears already exempts from its finite check on the PATCH side.
      const sanitizedDetails = { ...form.details };
      for (const field of Object.keys(DETAIL_FIELD_BOUNDS)) {
        if (!isDetailFieldValid(field, sanitizedDetails[field])) {
          sanitizedDetails[field] = null;
          // setForm is async, so this can't change what this same call posts (sanitizedDetails
          // above already carries the correct value) -- it exists so the field itself doesn't keep
          // showing a value the save just rejected, the same clear clearDetailIfInvalid does on
          // blur for a value the user types directly.
          clearDetailIfInvalid(field)();
        }
      }
      const propertyDetails = { ...form, details: sanitizedDetails };
      const { data } = await reilmAPI.savePropertyDetails({ propertyDetails });
      if (save) save(data?.data?.property);
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) return; // interceptor already redirecting to login
      setSaveError(
        err?.response?.data?.error?.message
        || 'Failed to save property. Please try again.'
      );
    } finally {
      // Released rather than left latched: the catch above leaves the drawer open with saveError
      // set so the user can fix and retry, and a permanently disabled button would strand them there.
      savingRef.current = false;
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------
  // form.details.amenities has two legitimate shapes: the dict transformDetails produces when it
  // comes straight from the API, or a plain string once the user has typed into the field (setDetails
  // overwrites the whole value on every keystroke, it doesn't know about the dict shape). Reading it
  // as the object unconditionally blanked the field the moment someone typed one character.
  const amenitiesValue = typeof form.details.amenities === 'string'
    ? form.details.amenities
    : form.details.amenities?.amenities_and_community_features?.[0] ?? '';

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="fixed top-0 right-0 h-full w-full md:w-[960px] z-[200] flex flex-col bg-brand-pageFill shadow-[-4px_0_24px_rgba(0,0,0,0.08)]">

      {/* Collapse toggle — desktop only. It floats outside the panel's own left edge (in the page
          area exposed to its left), which only exists at md+ where the panel doesn't fill the
          viewport; on mobile the panel is full-width so there's no exposed area to float it in.
          Mobile gets its own inline close button in the stepper row instead. */}
      <button
        onClick={handleCollapseToggle}
        className="hidden md:flex absolute -left-8 top-1/2 -translate-y-1/2 items-center justify-center w-16 h-16 rounded-full bg-white border border-brand-muted text-brand-dark hover:bg-brand-pageFill transition-colors z-10"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M6 4l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Decorative bg */}
      {/* <div className="absolute bottom-0 right-0 w-[540px] h-[560px] pointer-events-none overflow-hidden">
        <svg viewBox="0 0 540 560" fill="none" className="w-full h-full">
          <ellipse cx="420" cy="420" rx="340" ry="300" fill="#EEFBE7" />
          <ellipse cx="490" cy="500" rx="120" ry="110" fill="#DAE4D5" opacity="0.6" />
          <ellipse cx="320" cy="480" rx="220" ry="190" fill="#f8fef4" opacity="0.8" />
        </svg>
      </div> */}

      {/* Header. The two-step stepper went with the Contacts page: one step has nothing to step
          through, and the mock of this panel after the removal carries no stepper and a single
          button. The mobile close button lived in that row and has nowhere else to go, so the row
          itself stays. */}
      <div className="flex items-center pl-4 pr-4 md:pl-16 md:pr-[8rem] py-8 border-b border-brand-muted/40 z-10 shrink-0">
        <button
          type="button"
          onClick={handleCollapseToggle}
          className="md:hidden mr-3 shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-white border border-brand-muted text-brand-dark"
        >
          <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
            <path d="M6 4l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="text-[15px] font-semibold text-brand-dark">Property Details</span>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 md:px-16 py-6 z-10">

        <div className="space-y-5 max-w-[752px]">
            <p className="text-[13px] text-brand-secondary font-medium">Enter a property address to continue.</p>

            <Field label="Address">
              <AsyncSelect
                value={addressValue}
                loadOptions={loadOptions}
                onChange={onAddressChange}
                isClearable
                isMulti={false}
                placeholder="Search address..."
                noOptionsMessage={({ inputValue }) => {
                  if (inputValue.length <= 3) return 'Type 4+ characters to search';
                  return searchFailed ? 'Address lookup unavailable, try again' : 'No results';
                }}
                styles={rsStyles}
                components={{ DropdownIndicator: () => null }}
              />
              {detailFailed && (
                <p className="text-[12px] text-red-400 mt-[6px]">
                  Could not load property details, try again
                </p>
              )}
            </Field>

            {/* Beds / Baths / Sqft / Year Built */}
            {/* 16.08 (Set#5 item 1.1): Stage moved out of this row into Property Type's row below,
                per Cong's request. It sat here as a 5th flex-1 sibling next to four fixed-width
                fields with no responsive variant, so below desktop widths its share of the row went
                negative and the Select rendered crushed. */}
            <div className="flex flex-wrap gap-[19px] items-end">
              <div className="w-14">
                <Label>Beds</Label>
                <input type="text" value={form.details.beds} onChange={setDetails('beds')} onBlur={clearDetailIfInvalid('beds')} className={emDashCls} placeholder={EM_DASH} />
              </div>
              <div className="w-14">
                <Label>Baths</Label>
                <input type="text" value={form.details.baths} onChange={setDetails('baths')} onBlur={clearDetailIfInvalid('baths')} className={emDashCls} placeholder={EM_DASH} />
              </div>
              <div className="w-[98px]">
                <Label>Sqft</Label>
                <input type="text" value={form.details.sqft} onChange={setDetails('sqft')} onBlur={clearDetailIfInvalid('sqft')} className={emDashCls} placeholder={EM_DASH} />
              </div>
              <div className="w-[98px]">
                <Label>Year Built</Label>
                <input type="text" value={form.details.yearBuilt} onChange={setDetails('yearBuilt')} onBlur={clearDetailIfInvalid('yearBuilt')} className={emDashCls} placeholder={EM_DASH} />
              </div>
            </div>

            {/* Stage and Strategy are gone from this form. Both are still edited elsewhere -
                Stage from the Pipeline grid (StageCellEditor) and Strategy from Property Dashboard -
                so their paths stay in the backend's UPDATABLE_PROPERTY_PATHS. */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Property Type">
                <input type="text" value={form.propertyType} onChange={setTop('propertyType')} className={emDashCls} placeholder={EM_DASH} />
              </Field>
            </div>

            {/* Multi-Family (disabled stub) + Sewer */}
            {/* <div className="grid grid-cols-2 gap-4">
              <Field label="Multi-Family" muted>
                <div className={`${emDashCls} bg-brand-grayGreen opacity-50 cursor-not-allowed`} />
              </Field>
              <Field label="Sewer">
                <input type="text" value={form.details.sewer} onChange={setDetails('sewer')} className={emDashCls} placeholder={EM_DASH} />
              </Field>
            </div> */}

            {/* Lot Size + Flood Zone */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Lot Size">
                <input type="text" value={form.details.lotSize} onChange={setDetails('lotSize')} className={emDashCls} placeholder={EM_DASH} />
              </Field>
              <Field label="Flood Zone">
                <input type="text" value={form.details.floodZone} onChange={setDetails('floodZone')} className={emDashCls} placeholder={EM_DASH} />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Parking">
                <input type="text" value={form.details.parking} onChange={setDetails('parking')} className={emDashCls} placeholder={EM_DASH} />
              </Field>
            </div>

            {/* HOA */}
            <div>
              <SectionHeader>HOA</SectionHeader>
              <div className="grid grid-cols-2 gap-4">
                <Field label="HOA Fee">
                  <div className="flex h-10 rounded-lg border border-brand-muted bg-white overflow-hidden hover:border-brand-green focus-within:border-brand-green transition-colors">
                    <span className="flex items-center pl-3 text-[13px] font-medium text-brand-dark">$</span>
                    <input
                      type="text"
                      value={form.details.hoaFee}
                      onChange={setDetails('hoaFee')}
                      placeholder={EM_DASH}
                      className="flex-1 px-2 text-[13px] text-brand-dark placeholder:text-brand-placeholder outline-none"
                    />
                    <span className="flex items-center pr-3 text-[12px] text-brand-muted"></span>
                  </div>
                </Field>
                <Field label="Amenities">
                  <input
                    type="text"
                    value={amenitiesValue}
                    onChange={setAmenitiesText}
                    className={emDashCls}
                    placeholder={EM_DASH}
                  />
                </Field>
              </div>
            </div>

            {/* Schools */}
            <div>
              <SectionHeader>Schools</SectionHeader>
              <div className="grid grid-cols-3 gap-x-[16px]">
                {[
                  { label: 'Elementary School', nameField: 'elementary', rateField: 'elementary_state_rate' },
                  { label: 'Middle School',      nameField: 'middle',     rateField: 'middle_state_rate'     },
                  { label: 'High School',        nameField: 'high',       rateField: 'high_state_rate'       },
                ].map(({ label, nameField, rateField }) => (
                  <div key={label}>
                    {/* 16.08 (Set#5 item 1.2): "Elementary School" wraps to two lines at the
                        mobile column width, "Middle School"/"High School" don't, so their inputs
                        sat one line higher and the row didn't line up. Reserving two lines' height
                        (measured) fixes it there; at the wider desktop column all three labels
                        already fit on one line, so md:min-h-0 drops the reservation rather than
                        add unrequested whitespace above a row that was already aligned. */}
                    <div className="min-h-[39px] md:min-h-0">
                      <Label>{label}</Label>
                    </div>
                    <div className="flex items-center gap-x-[8px] mt-[6px]">
                      {form.details.schools[rateField] != null && form.details.schools[rateField] !== '' && (
                        <span
                          className="w-[44px] h-[44px] rounded-full flex items-center justify-center text-white font-bold shrink-0"
                          style={{ backgroundColor: getScoreBg(parseInt(form.details.schools[rateField], 10)) }}
                        >
                          <span style={{ fontSize: 15 }}>{form.details.schools[rateField]}</span>
                          <span style={{ fontSize: 10, fontWeight: 400 }}>/10</span>
                        </span>
                      )}
                      <input
                        type="text"
                        value={form.details.schools[nameField]}
                        onChange={setSchool(nameField)}
                        className={emDashCls}
                        placeholder={EM_DASH}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Taxes */}
            <div className="pb-6">
              <SectionHeader>Taxes</SectionHeader>
              <div className="grid grid-cols-2 gap-4 w-full md:w-[486px]">
                <Field label="Tax Year">
                  <input type="text" value={form.details.tax.year} onChange={setTax('year')} className={emDashCls} placeholder={EM_DASH} />
                </Field>
                <Field label="Total Tax">
                  <input type="text" value={form.details.tax.amount} onChange={setTax('amount')} className={emDashCls} placeholder={EM_DASH} />
                </Field>
              </div>
            </div>
        </div>

      </div>

      {/* Footer */}
      <div className="flex justify-end items-center gap-3 pl-4 pr-4 md:pl-16 md:pr-[8rem] py-5  z-10 shrink-0 bg-brand-pageFill">
        {saveError && (
          <p className="text-[12px] text-red-400 mr-auto">{saveError}</p>
        )}
        {/* Back and Next went with the Contacts page. Finish is the only action left, and it was
            already the only one that saves. No spinner and no label change while saving -- either
            would need a Figma value we don't have; disabling via `saving` is the behavioural fix.
            The concurrency guard itself is savingRef, checked and set synchronously above -- this
            attribute is a visual mirror of it, not the mechanism that blocks a second click. */}
        <button
          onClick={addressFilled ? handleFinish : undefined}
          disabled={!addressFilled || saving}
          className={`px-8 h-14 rounded-full text-[15px] font-medium transition-colors ${
            addressFilled && !saving
              ? 'bg-brand-green text-white hover:bg-[#3da814]'
              : 'bg-[#C9D8C4] text-white cursor-not-allowed'
          }`}
        >
          Finish
        </button>
      </div>
    </div>
  );
}

export default AddOpportunity;
